import { browser } from '$app/environment';
import type { RequestEvent } from '@sveltejs/kit';

// Cache for DOMPurify to avoid repeated imports
let DOMPurifyCache: any = null;

// Lazy load DOMPurify
const getDOMPurify = async () => {
	if (!browser) return null;

	if (!DOMPurifyCache) {
		try {
			const module = await import('dompurify');
			DOMPurifyCache = module.default;
		} catch (error) {
			console.warn('DOMPurify not available, using basic sanitization:', error);
			DOMPurifyCache = null;
		}
	}

	return DOMPurifyCache;
};

// Cache for security monitor to avoid repeated imports
let securityMonitorCache: any = null;

// Lazy load security monitor (server-side only) - DISABLED FOR BUILD
// const getSecurityMonitor = async () => {
// Temporarily disabled to fix build issues
// return null;

/*
	if (browser) return null;

	if (!securityMonitorCache) {
		try {
			const module = await import('$lib/server/securityMonitor');
			securityMonitorCache = module.securityMonitor;
		} catch (error) {
			console.warn('Security monitor not available:', error);
			securityMonitorCache = null;
		}
	}

	return securityMonitorCache;
	*/
// };

export const SecureSanitizer = {
	/**
	 * Sanitize HTML content
	 * @param content The HTML content to sanitize
	 * @param options Optional options for DOMPurify (browser only)
	 * @param event Optional SvelteKit event (server only, for monitoring)
	 * @param source Optional string for source context (e.g. 'body', 'url', etc.)
	 */
	sanitize: async (
		content: string,
		options?: any,
		event?: RequestEvent,
		source: 'body' | 'url' | 'headers' | 'query' = 'body'
	): Promise<string> => {
		if (!content) return '';

		// For SSR, use basic sanitization
		if (!browser) {
			const dangerous = SecureSanitizer.isDangerous(content);
			let sanitized = content
				.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
				.replace(/javascript:/gi, '')
				.replace(/on\w+\s*=/gi, '')
				.replace(/data:text\/html/gi, '')
				.replace(/vbscript:/gi, '');
			if (dangerous && event) {
				// Security monitoring temporarily disabled for build
				// const securityMonitor = await getSecurityMonitor();
				// if (securityMonitor) {
				// 	securityMonitor.monitorXSSAttempt(event, {
				// 		payload: content,
				// 		source,
				// 		blocked: true
				// 	});
				// }
			}
			return sanitized;
		}

		// For browser, use DOMPurify for full sanitization
		const DOMPurify = await getDOMPurify();
		if (DOMPurify) {
			const sanitized = DOMPurify.sanitize(content, {
				ALLOWED_TAGS: [
					'a',
					'b',
					'blockquote',
					'code',
					'del',
					'dd',
					'dl',
					'dt',
					'em',
					'i',
					'img',
					'kbd',
					'li',
					'ol',
					'pre',
					'q',
					's',
					'strike',
					'strong',
					'sub',
					'sup',
					'u',
					'ul',
					'br',
					'p',
					'span',
					'div',
					'h1',
					'h2',
					'h3',
					'h4',
					'h5',
					'h6'
				],
				ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'id', 'target', 'rel'],
				...options
			}) as unknown as string;
			return sanitized;
		} else {
			// Fallback to basic sanitization
			return content
				.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
				.replace(/javascript:/gi, '')
				.replace(/on\w+\s*=/gi, '')
				.replace(/data:text\/html/gi, '')
				.replace(/vbscript:/gi, '');
		}
	},

	/**
	 * Sanitize text content (remove all HTML)
	 * @param text The text to sanitize
	 * @param event Optional SvelteKit event (server only, for monitoring)
	 * @param source Optional string for source context
	 */
	sanitizeText: async (
		text: string,
		event?: RequestEvent,
		source: 'body' | 'url' | 'headers' | 'query' = 'body'
	): Promise<string> => {
		if (!text) return '';

		// For SSR, use basic HTML removal
		if (!browser) {
			const dangerous = SecureSanitizer.isDangerous(text);
			let sanitized = text.replace(/<[^>]*>/g, '');
			if (dangerous && event) {
				// Security monitoring temporarily disabled for build
				// const securityMonitor = await getSecurityMonitor();
				// if (securityMonitor) {
				// 	securityMonitor.monitorXSSAttempt(event, {
				// 		payload: text,
				// 		source,
				// 		blocked: true
				// 	});
				// }
			}
			return sanitized;
		}

		// For browser, use DOMPurify for full sanitization
		const DOMPurify = await getDOMPurify();
		if (DOMPurify) {
			const sanitized = DOMPurify.sanitize(text, {
				ALLOWED_TAGS: [],
				ALLOWED_ATTR: []
			}) as unknown as string;
			return sanitized;
		} else {
			// Fallback to basic HTML removal
			return text.replace(/<[^>]*>/g, '');
		}
	},

	/**
	 * Sanitize URL (treat as text)
	 */
	sanitizeUrl: async (url: string): Promise<string> => {
		return SecureSanitizer.sanitizeText(url);
	},

	/**
	 * Check if content contains potentially dangerous elements
	 */
	isDangerous: (content: string): boolean => {
		if (!content) return false;

		const dangerousPatterns = [
			/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
			/javascript:/gi,
			/on\w+\s*=/gi,
			/data:text\/html/gi,
			/vbscript:/gi
		];

		return dangerousPatterns.some((pattern) => pattern.test(content));
	}
};
